import { AfterViewInit, Component, ElementRef, Input, OnChanges, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgOptionTemplateDirective, NgSelectModule } from '@ng-select/ng-select';
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { COMPLIANCE_URL } from '../core/compliance-link';
import { GmrDataService } from '../core/gmr-data.service';
import { GmrFunctionParameter, GmrLovRow } from '../core/gmr.models';
import { HighlightPipe } from '../shared/pipes/highlight.pipe';
import { isParameterMissing } from './parameter-validation';

//
// 1:1 port of the two mutually-exclusive <ui-select> blocks in menu.html (single LOV vs the
// hardcoded "gmr-eol-lov-divisions-multi" multi-select) plus the hidden-input mirror comment
// explains why it exists: "Fix ui-select not posting values by setting a hidden input with the
// selected value" - true for ng-select too, since it's the same kind of <div>-based custom
// widget with no form-association the browser's native form submit can pick up on its own. The
// hidden input is updated imperatively via ElementRef, matching the original's synchronous
// jQuery .val() call, deliberately not via a declarative [value] binding - see migration plan
// risk register on why that distinction matters here.
//
@Component({
  selector: 'app-lov-select',
  standalone: true,
  imports: [FormsModule, NgSelectModule, NgOptionTemplateDirective, HighlightPipe, TranslatePipe, TranslateDirective],
  styles: [':host { display: contents; }'],
  templateUrl: './lov-select.component.html',
})
export class LovSelectComponent implements OnChanges, AfterViewInit
{
  @Input({ required: true }) parameter!: GmrFunctionParameter;
  @Input() lovRows: GmrLovRow[] = [];

  //
  // The id the owning <label for> points at, computed once by ParameterFieldComponent so that both
  // sides cannot drift apart. Handed to ng-select as [labelForId], which is how it exposes the id of
  // the real, focusable search input it renders internally - the only element here a label can
  // usefully name, since the input that actually carries the value into the submit is the hidden
  // mirror below and a hidden input can neither be focused nor labelled.
  //
  @Input({ required: true }) fieldId!: string;

  @ViewChild('hiddenMirror') private hiddenMirrorRef!: ElementRef<HTMLInputElement>;

  private readonly data = inject(GmrDataService);
  private readonly translate = inject(TranslateService);
  private readonly hostRef: ElementRef<HTMLElement> = inject(ElementRef);

  protected readonly complianceUrl = COMPLIANCE_URL;

  //
  // Keeps the hidden mirror in sync whenever `parameter` changes for a reason other than the
  // user interacting with THIS select - e.g. a dependent LOV being reset by lovSelectionChanged
  // cascading from a different field, or the async selectAllDivisions()/selectDefaultDivisions().
  // ngOnChanges can fire before the view (and so @ViewChild) exists - on the very first binding,
  // ngAfterViewInit's own call covers the initial sync instead.
  //
  ngOnChanges(): void
  {
    if (this.hiddenMirrorRef?.nativeElement)
    {
      this.updateHiddenMirror();
      this.updateValidity();
    }
  }

  ngAfterViewInit(): void
  {
    this.updateHiddenMirror();
    this.updateValidity();
  }

  get isMulti(): boolean
  {
    return this.parameter.cfr_lov_preset === 'gmr-eol-lov-divisions-multi';
  }

  //
  // 1:1 port of buildUnavailableStats() in gmr-app.js: a notice listing why some divisions are
  // missing from the LOV (inactive / outside service area / unknown reason / non-compliant with
  // policy). The original injected this via jQuery into the ui-select element whose id happened
  // to equal the divisions parameter's cfr_code ("division_codes") - here it's just rendered as
  // a sibling of <ng-select> in the template, gated on the same isMulti special-case already used
  // by selectAll()/selectDefault() for this one hardcoded LOV preset.
  //
  get unavailableStatsEntries(): { count: number; textKey: string; className: string; title: string }[]
  {
    const stats = this.data.unavailableStats();
    if (!stats) return [];

    return [
      { count: stats.inactive_cnt, textKey: 'itgen_gmr_inactive_cnt', className: 'inactiveCount', title: stats.inactive_list ?? '' }
    , { count: stats.outside_service_area_cnt, textKey: 'itgen_gmr_outside_cnt', className: 'outsideSACount', title: stats.outside_service_area_list ?? '' }
    , { count: stats.unknown_cnt, textKey: 'itgen_gmr_unknown_cnt', className: 'unknownCount', title: stats.unknown_list ?? '' }
    , { count: stats.compliance_cnt, textKey: 'itgen_gmr_compliance_cnt', className: 'complianceCount', title: stats.compliance_list ?? '' }
    ].filter(entry => entry.count > 0);
  }

  //
  // 1:1 port of ui-select-choices' propsFilter usage: substring match, case-insensitive, on
  // either code or description.
  //
  searchFn = (term: string, item: GmrLovRow): boolean =>
  {
    const needle = term.toLowerCase();
    return String(item.code ?? '').toLowerCase().includes(needle) || String(item.description ?? '').toLowerCase().includes(needle);
  };

  onChange(): void
  {
    this.updateHiddenMirror();
    this.updateValidity();
    this.data.lovSelectionChanged(this.parameter);
  }

  //
  // Makes the browser's own constraint validation cover this widget, which it otherwise cannot: the
  // input that carries this field's value into the form submit is the type="hidden" mirror below, and
  // hidden inputs are barred from constraint validation entirely. A `required` attribute on it would be
  // ignored, and putting one on an invisible stand-in is worse than useless - Chrome refuses to show a
  // validation bubble for a control it cannot focus and blocks the submit with nothing but a console
  // warning, which is exactly the silent dead-button this replaced.
  //
  // ng-select does render one real, visible, focusable <input> (its search field), so the message is
  // attached there via setCustomValidity: the browser then blocks the submit, shows its native bubble
  // anchored at the dropdown and focuses it, identically to a required <input> elsewhere on the form.
  // That input carries no `name`, so it contributes nothing to the submitted data.
  //
  // Queried from the DOM rather than through NgSelectComponent's own ViewChild: both are internals of
  // the library, and a missing element here degrades safely (ParameterFormComponent.onSubmit still
  // refuses the download), whereas a renamed @ViewChild property would be a compile-time break.
  //
  private updateValidity(): void
  {
    const input = this.hostRef.nativeElement.querySelector<HTMLInputElement>('ng-select input');
    if (!input) return;

    if (!isParameterMissing(this.parameter))
    {
      input.setCustomValidity('');
      return;
    }

    //
    // instant() rather than an async get(): by the time a parameter form exists the catalog response has
    // already run translate.use(), so the table is loaded. Worst case the raw key shows up in a bubble,
    // which is visible and reportable rather than silently absent.
    //
    const key = this.isMulti ? 'itgen_gmr_select_division_required' : 'itgen_gmr_field_required';
    input.setCustomValidity(this.translate.instant(key));
  }

  selectAll(): void
  {
    this.data.selectAllDivisions();
    // The selection is fetched asynchronously; the mirror is refreshed once it lands, see
    // ParameterFieldComponent's effect on cfr_default_value_arr.
  }

  selectDefault(): void
  {
    this.data.selectDefaultDivisions();
  }

  updateHiddenMirror(): void
  {
    const value = this.isMulti
      ? (this.parameter.cfr_default_value_arr ?? []).join(',')
      : (this.parameter.cfr_default_value ?? '');
    this.hiddenMirrorRef.nativeElement.value = String(value ?? '');
  }
}
